import _ from 'lodash';
import Immutable from 'seamless-immutable';
import RefData from '../../ref-data';
import { assetRefService } from '../../common/Services';
import { resultTypes, quizTypes } from '../../common/enums';
import { uniqueId } from '../../common/tools';
import { fieldSpacing } from '../../common/formFields/sectionFieldSpacing.js';
import { fieldSizes } from '../../common/formFields/sectionFieldSizes.js';
import { fieldLayouts } from '../../common/formFields/sectionFieldLayouts.js';
import { fieldStyles } from '../../common/formFields/sectionFieldStyles.js';

/**
 * Form
 * @typedef {Object} Form
 * @property {string} byline - a description of what the form is used for
 * @property {string} headline -the form headline, sort of like an alternative form name
 * @property {number} id - the form id
 * @property {string} lastUpdated - datetime audit string for last time record was updated
 * @property {string} lastUpdatedMillis - time of last modified form in milliseconds
 * @property {Object} layoutConfig - the JSON that describes the form layout
 * @property {Object} modifiedBy - email of the person to last modify the form
 * @property {string} name - the form name
 * @property {string} shortByline - a short description of what the form is used for
 * @property {RefData} typeRef - the form typeRef [type.displayobject.*]
 */

const defaultFormLayout = {
  modules:    [],
  pageLayout: {
    sections: [
      {
        type: 'content',
        rows: [{ columns: [{ modules: [] }], type: '1col_full' }],
        data: { linkText: '', style: '' },
      },
    ],
  },
};

const createScoreResult = (minScore = 0, maxScore = 0, message = '', scoreMessage = '', id) => ({
  imageAssetRefAltText: '',
  imageAssetRef:        '',
  maxScore,
  message,
  minScore,
  scoreMessage,
  shareDescription:     '',
  thumbnailAssetRef:    '',
  url:                  '',
  id,
});

const createCategoryResult = (selected, title, message, id) => ({
  imageAssetRefAltText: '',
  imageAssetRef:        '',
  message:              title,
  title:                message,
  shareDescription:     '',
  thumbnailAssetRef:    '',
  url:                  '',
  ...(selected !== null && { selected }),
  id,
  resultValue:          '',
});

/**
 * Create a FormModel from provided data
 */
function create(form = {}) {
  const result = _.clone(form);

  result.typeRef = _.isString(form.typeRef) ? RefData.Model.hydrate(form.typeRef) : form.typeRef;
  result.validFor =
    !!form.validFor && _.isString(form.validFor) ? RefData.Model.hydrate(form.validFor) : form.validFor || '';
  result.classRef = { headline: '', byline: '', id: '' };

  return Immutable(result, { prototype: model });
}

/**
 * function builds a new form config, given the parameters
 */
function createDefaultFormConfig({ id, name, typeRef, validFor, quizType, resultType }) {
  const dateNow = new Date().getTime();
  const formDef = {
    id:           id,
    layoutConfig: {
      pageLayout: {
        sections: [
          {
            type:      'form',
            sectionId: dateNow.toString(),
            data:      {
              size:                    fieldSizes.STANDARD,
              fieldStyle:              fieldStyles.OUTLINE,
              layout:                  fieldLayouts.TOP,
              fieldSpacing:            fieldSpacing.CONTENT,
              inverted:                false,
              hiddenFields:            [],
              sectionTitle:            '',
              sectionLabel:            'Form Section',
              customCSS:               '',
              linkText:                'Submit',
              style:                   '',
              errorMessage:            '',
              backgroundImageAssetRef: '',
              customizeErrorMessage:   true,
            },
            rows:            [],
            sectionSettings: {
              linkText: 'Submit button',
              style:    '',
            },
          },
        ],
      },
      modules: [],
    },
    name:        name,
    typeRef:     { id: typeRef },
    validFor:    { id: validFor },
    lastUpdated: '',
    modifiedBy:  App.globalContext.user.email,
    thumbnail:   '',
  };

  if (typeRef === RefData.Enum.TYPE.FORM.QUIZ) {
    let quizResults;

    const DEFAULT_MESSAGE = 'e.g. Thanks for entering our quiz. Now find out what your favourite city is.';

    switch (quizType) {
      case quizTypes.SCORE:
        const DEFAULT_SCORE_MESSAGE = 'You can do better. You have scored ${score} out of ${total}!';
        quizResults = [
          createScoreResult(0, 100, DEFAULT_MESSAGE, DEFAULT_SCORE_MESSAGE, 'result-id-1'),
          createScoreResult(101, 200, DEFAULT_MESSAGE, DEFAULT_SCORE_MESSAGE, 'result-id-2'),
          createScoreResult(201, 300, DEFAULT_MESSAGE, DEFAULT_SCORE_MESSAGE, 'result-id-3'),
        ];
        break;

      case quizTypes.CATEGORY_MATCH:
        //[Victor] FOR FUTURE REFERENCE: If we do decide to add a default generator for quizTypes.CATEGORY_INTERSECT, please be aware that we have a specific method to generate the ID for the CATEGORY_INTERSECT results --- look for the usage of `maxCategoryResultId`
        quizResults = [
          createCategoryResult(true, 'Result 1', DEFAULT_MESSAGE, uniqueId()),
          createCategoryResult(false, 'Result 2', DEFAULT_MESSAGE, uniqueId()),
          createCategoryResult(false, 'Result 3', DEFAULT_MESSAGE, uniqueId()),
        ];
        break;
      case PRODUCT_FEED:
        //PRODUCT_FEED has their result handled externally, so when we generate a new form we should make sure that the contents of the results are empty
        quizResults = [];
    }

    _.set(formDef, 'layoutConfig.pageLayout.sections[0].data.quiz', {
      quizType:   quizType,
      resultType: resultType,
      results:    quizResults,
      ...(quizType === quizTypes.CATEGORY_INTERSECT
        ? {
          maxCategoryResults:  1,
          maxCategoryResultId: 3,
          useRandomResults:    true,
        }
        : { quizMaxScore: 0 }),
    });
  }
  return create(formDef);
}

/**
 * Transforms a backend form object into a frontend Form Model
 * @param {Object} form - backend formatted form
 * @returns {Form}
 */
function deserialize(form) {
  const result = _.pick(form, _keys);
  result.thumbnail = assetRefService.buildAssetRef(
    'static',
    'image',
    'auto',
    'c/img/experience-workspace/displayobject-landing-slide.svg'
  );

  result.layoutConfig = form.layoutConfig ? JSON.parse(form.layoutConfig) : Object.assign({}, defaultFormLayout);

  return create(result);
}

/**
 * Transform a FormModel into a backend form object
 * @returns {Object}
 */
function serialize(form, removeId = false) {
  const result = form.asMutable({ deep: true });
  result.layoutConfig = JSON.stringify(result.layoutConfig);
  result.typeRef = form.typeRef.id;
  result.validFor = _.isObject(form.validFor) ? form.validFor.id : form.validFor || ''; //need to work with empty validFor
  result.classRef = form.classRef.id;
  if (removeId) {
    delete result.id;
  }
  return _.pick(result, _keys);
}

/** The FormModel prototype */
const model = {
  assetRef(displayType) {
    return assetRefService.buildAssetRef('static', 'image', 'auto', 'c/img/icons/screen/screen-form.svg');
  },
  heading: '',
  get headline() {
    return this.classRef.headline;
  },
  get byline() {
    return this.classRef.byline;
  },
  get shortByline() {
    return this.classRef.byline ? this.classRef.byline.split('.')[0] : '';
  },
};

/**
 * An array of keys on the API Theme object that should be shared between the API requests and responses
 */
const _keys = [
  'classRef',
  'heading',
  'id',
  'lastUpdated',
  'layoutConfig',
  'name',
  'modifiedBy',
  'thumbnail',
  'typeRef',
  'validFor',
  'containerRef',
];

export default {
  create,
  deserialize,
  serialize,
  createDefaultFormConfig,
};
